
import { useState, useEffect, useContext } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Resource } from "../hooks/useResourceManagement";
import { ProjectContext } from "../ProjectStructureTab";

interface ResourceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (resource: any) => Promise<boolean>;
  resource?: Resource | null;
  isEdit?: boolean;
}

const ResourceDialog = ({
  open,
  onOpenChange,
  onSave,
  resource,
  isEdit = false
}: ResourceDialogProps) => {
  const { selectedProject } = useContext(ProjectContext);
  
  const [formData, setFormData] = useState({
    name: "",
    role: "",
    role_type: "team-member",
    skillset: "",
    capacity: "",
    rate: "",
    availability: "",
    years_experience: "",
    past_projects: "",
    email: "",
    project_id: selectedProject?.id || null,
    project_name: selectedProject?.name || null
  });

  const [emailError, setEmailError] = useState("");

  // Email validation function
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  useEffect(() => {
    if (isEdit && resource) {
      setFormData({
        name: resource.name || "",
        role: resource.role || "",
        role_type: resource.role_type || "team-member",
        skillset: resource.skillset || "",
        capacity: resource.capacity || "",
        rate: resource.rate !== null && resource.rate !== undefined ? resource.rate.toString() : "",
        availability: resource.availability || "",
        years_experience: resource.years_experience ? resource.years_experience.toString() : "",
        past_projects: resource.past_projects || "",
        email: resource.email || "",
        project_id: resource.project_id || selectedProject?.id || null,
        project_name: resource.project_name || selectedProject?.name || null
      });
      setEmailError("");
    } else {
      // Reset form for new resource
      setFormData({
        name: "",
        role: "",
        role_type: "team-member",
        skillset: "",
        capacity: "",
        rate: "",
        availability: "",
        years_experience: "",
        past_projects: "",
        email: "",
        project_id: selectedProject?.id || null,
        project_name: selectedProject?.name || null
      });
      setEmailError("");
    }
  }, [isEdit, resource, open, selectedProject]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.role.trim()) {
      return;
    }

    // Validate email if provided
    if (formData.email.trim() && !validateEmail(formData.email.trim())) {
      setEmailError("Please enter a valid email address");
      return;
    }
    
    setEmailError("");

    const resourceData = {
      name: formData.name.trim(),
      role: formData.role.trim(),
      role_type: formData.role_type,
      skillset: formData.skillset.trim() || null,
      capacity: formData.capacity.trim() || null,
      rate: formData.rate !== "" ? parseFloat(formData.rate.replace(/,/g, "")) : null,
      availability: formData.availability.trim() || null,
      years_experience: formData.years_experience ? parseInt(formData.years_experience) : null,
      past_projects: formData.past_projects.trim() || null,
      email: formData.email.trim() || null,
      project_id: formData.project_id,
      project_name: formData.project_name
    };

    const success = await onSave(resourceData);
    if (success) {
      onOpenChange(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear email error when user starts typing
    if (field === "email") {
      setEmailError("");
    }
  };

  const handleEmailBlur = () => {
    if (formData.email.trim() && !validateEmail(formData.email.trim())) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  const formatRateWithCommas = (value: string) => {
    if (value.includes('.')) return value;
    const num = parseFloat(value.replace(/,/g, ''));
    if (isNaN(num)) return value;
    return num >= 1000 ? num.toLocaleString('en-US') : value;
  };

  const handleRateInputChange = (value: string) => {
    // Remove commas and minus signs
    const cleaned = value.replace(/,/g, '').replace(/-/g, '');
    if (cleaned === '') {
      setFormData(prev => ({ ...prev, rate: '' }));
      return;
    }
    // Only allow digits and optional decimal point
    if (!/^\d*\.?\d*$/.test(cleaned)) return;
    const num = parseFloat(cleaned);
    if (isNaN(num)) return;
    if (num < 0) {
      setFormData(prev => ({ ...prev, rate: '0' }));
      return;
    }
    if (num > 100000) {
      setFormData(prev => ({ ...prev, rate: '100000' }));
      return;
    }
    setFormData(prev => ({ ...prev, rate: cleaned }));
  };

  const handleNumericInputChange = (field: string, value: string) => {
    // Remove any minus signs from the input
    const cleanValue = value.replace(/-/g, '');
    
    // Allow empty string for clearing the field
    if (cleanValue === "") {
      setFormData(prev => ({ ...prev, [field]: cleanValue }));
      return;
    }
    
    // Only allow numeric characters and decimal point
    if (!/^\d*\.?\d*$/.test(cleanValue)) {
      return;
    }
    
    // Convert to number and validate based on field type
    const numValue = parseFloat(cleanValue);
    
    if (isNaN(numValue)) {
      return;
    }
    
    // Field-specific validation
    let isValid = false;
    
    switch (field) {
      case "capacity": // Hours/Day: 0 < value <= 16
        isValid = numValue > 0 && numValue <= 16;
        break;
      case "rate": // Rate/Hour: 0 < value <= 100000
        isValid = numValue > 0 && numValue <= 100000;
        break;
      case "years_experience": // Years Experience: 0 < value < 50
        isValid = numValue > 0 && numValue < 50;
        break;
      default:
        isValid = numValue > 0;
    }
    
    if (isValid) {
      setFormData(prev => ({ ...prev, [field]: cleanValue }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white text-black max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-black">
            {isEdit ? "Edit Resource" : "Add New Resource"}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="name" className="text-black text-sm">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                className="bg-white border-gray-300 text-black h-9"
                required
              />
            </div>
            
            <div className="space-y-1">
              <Label htmlFor="role" className="text-black text-sm">Role *</Label>
              <Input
                id="role"
                value={formData.role}
                onChange={(e) => handleInputChange("role", e.target.value)}
                className="bg-white border-gray-300 text-black h-9"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="role_type" className="text-black text-sm">Role Type</Label>
              <Select value={formData.role_type} onValueChange={(value) => handleInputChange("role_type", value)}>
                <SelectTrigger className="bg-white border-gray-300 text-black h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="project-director">Project Director</SelectItem>
                  <SelectItem value="project-manager">Project Manager</SelectItem>
                  <SelectItem value="team-lead">Team Lead</SelectItem>
                  <SelectItem value="team-member">Team Member</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="capacity" className="text-black text-sm">Hours/Day</Label>
              <Input
                id="capacity"
                value={formData.capacity}
                onChange={(e) => handleNumericInputChange("capacity", e.target.value)}
                className="bg-white border-gray-300 text-black h-9"
                placeholder="e.g., 8 (max 16)"
                min="0.1"
                max="16"
                step="0.1"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="rate" className="text-black text-sm">Rate/Hour ($)</Label>
              <Input
                 id="rate"
                 type="text"
                 value={formatRateWithCommas(String(formData.rate ?? ''))}
                 onChange={(e) => handleRateInputChange(e.target.value)}
                 className="bg-white border-gray-300 text-black h-9"
                 placeholder="e.g., 75 (max $100,000)"
               />
            </div>

            <div className="space-y-1">
              <Label htmlFor="years_experience" className="text-black text-sm">Years Experience</Label>
              <Input
                id="years_experience"
                type="number"
                value={formData.years_experience}
                onChange={(e) => handleNumericInputChange("years_experience", e.target.value)}
                className="bg-white border-gray-300 text-black h-9"
                placeholder="e.g., 5 (max 49)"
                min="0.1"
                max="49"
                step="0.1"
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="email" className="text-black text-sm">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              onBlur={handleEmailBlur}
              className={`bg-white border-gray-300 text-black h-9 ${emailError ? 'border-red-500' : ''}`}
              placeholder="e.g., john.doe@company.com"
            />
            {emailError && (
              <p className="text-red-500 text-xs mt-1">{emailError}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="skillset" className="text-black text-sm">Skillset</Label>
            <Textarea
              id="skillset"
              value={formData.skillset}
              onChange={(e) => handleInputChange("skillset", e.target.value)}
              className="bg-white border-gray-300 text-black min-h-[60px] resize-none"
              placeholder="e.g., React, TypeScript, Node.js"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="availability" className="text-black text-sm">Availability</Label>
            <Input
              id="availability"
              value={formData.availability}
              onChange={(e) => handleInputChange("availability", e.target.value)}
              className="bg-white border-gray-300 text-black h-9"
              placeholder="e.g., Full-time, Part-time, Weekends"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="past_projects" className="text-black text-sm">Past Projects</Label>
            <Textarea
              id="past_projects"
              value={formData.past_projects}
              onChange={(e) => handleInputChange("past_projects", e.target.value)}
              className="bg-white border-gray-300 text-black min-h-[60px] resize-none"
              placeholder="Brief description of relevant past projects"
            />
          </div>

          {selectedProject && (
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Project:</span> {selectedProject.name}
              </p>
            </div>
          )}

          <DialogFooter className="gap-2 pt-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-gray-300 bg-white text-black hover:bg-gray-50"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-primary hover:bg-primary/90 text-black"
            >
              {isEdit ? "Update Resource" : "Add Resource"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ResourceDialog;
